import { TinyColor } from '@ctrl/tinycolor';
import {
  FontAwesomeIcon,
  FontAwesomeIconStyle,
} from '@fortawesome/react-native-fontawesome';
import * as React from 'react';
import { ColorValue, StyleSheet, ViewStyle } from 'react-native';
import { Pressable } from 'react-native-web-hover';
import { color, margins, spacing } from '../../theme';
import Text from '../Text';
import { ButtonPresetNames, textPresets, viewPresets } from './Button.presets';
import { ButtonProps } from './Button.props';

const paddings: { [key: string]: ViewStyle } = {
  small: {
    height: 40,
    paddingHorizontal: spacing[3],
  },
  normal: {
    height: 56,
    paddingHorizontal: spacing[5],
  },
  large: {
    height: 66,
    paddingHorizontal: spacing[6],
  },
};

const baseIconStyle = {
  ...margins.mr3,
};

const iconPresetColourMap: { [key: string]: FontAwesomeIconStyle } = {
  primary: {
    color: color.white,
  },
  secondary: {
    color: color.primary,
  },
  tertiary: {
    color: color.primary,
  },
  text: {
    color: color.primary,
  },
};

/**
 * Button component.
 */
export function Button(props: ButtonProps): JSX.Element {
  // grab the props
  const {
    preset = 'primary',
    size = 'normal',
    text,
    style: styleOverride,
    textStyle: textStyleOverride,
    iconStyle: iconStyleOverride,
    children,
    icon,
    round,
    ...rest
  } = props;

  const padding = paddings[size];

  const viewStyle = StyleSheet.flatten([
    viewPresets[preset],
    padding,
    styleOverride,
    round ? { borderRadius: 50 } : undefined,
  ]);
  const textStyle = StyleSheet.flatten([
    textPresets[preset],
    textStyleOverride,
  ]);

  const iconStyle = StyleSheet.flatten([
    iconPresetColourMap[preset],
    children || text ? baseIconStyle : undefined,
    iconStyleOverride,
  ]) as FontAwesomeIconStyle;

  const content =
    children ||
    (text ? (
      <Text weight="semibold" style={textStyle}>
        {text}
      </Text>
    ) : null);

  return (
    <Pressable
      style={({ hovered, pressed }) => [
        viewStyle,
        {
          /** Compute the background and border colors with hover states */
          backgroundColor: hovered
            ? getHoverColor[preset]
            : viewStyle.backgroundColor,
          borderColor:
            hovered && preset === 'tertiary' && viewStyle.borderColor
              ? new TinyColor(viewStyle.borderColor).darken(20).toHexString()
              : viewStyle.borderColor,
          opacity: pressed ? 0.7 : 1,
        },
      ]}
      {...rest}
    >
      {icon ? <FontAwesomeIcon style={iconStyle} icon={icon} /> : null}

      {content}
    </Pressable>
  );
}

const getHoverColor: Record<ButtonPresetNames, ColorValue> = {
  primary: new TinyColor(color.primary).darken(30).toHexString(),
  secondary: new TinyColor(color.secondary).darken(10).toHexString(),
  tertiary: color.white,
  text: color.white,
};
